import { getAuthenticatedClient } from '@/services/client';

// FIX-CCS-AUDIO-PRIVACY (PD-7): voice-messages is a PRIVATE bucket shared with CCS (agxl-crop).
// CBS reaches farmer audio only through short-lived SIGNED URLs, scoped by the PIN session's
// x-session-token via storage RLS. Mirrors the agxl-crop helper of the same name.

export const SIGNED_AUDIO_TTL_SECONDS = 3600;

/** Extract the object PATH from a stored value that may be a bare path OR a legacy full public URL. */
export function storagePathFromStored(stored: string | null | undefined, bucket: string): string | null {
  const s = (stored ?? '').trim();
  if (!s) return null;
  for (const marker of [`/object/public/${bucket}/`, `/object/sign/${bucket}/`, `/object/${bucket}/`]) {
    const i = s.indexOf(marker);
    if (i >= 0) return s.slice(i + marker.length).split('?')[0];
  }
  if (s.startsWith(`${bucket}/`)) return s.slice(bucket.length + 1);
  return s;
}

/** Short-lived SIGNED URL for a private-bucket audio object. Additive: returns null on failure. */
export async function getSignedAudioUrl(
  stored: string | null | undefined,
  bucket: string,
  ttl: number = SIGNED_AUDIO_TTL_SECONDS,
): Promise<string | null> {
  const path = storagePathFromStored(stored, bucket);
  if (!path) return null;
  try {
    const client = getAuthenticatedClient();
    const { data, error } = await client.storage.from(bucket).createSignedUrl(path, ttl);
    if (error || !data?.signedUrl) return null;
    return data.signedUrl;
  } catch {
    return null;
  }
}

// FIX-CCS-PRIVACY-COMPLETE: photos + avatars are now PRIVATE too. Same signing idiom for images.
export const getSignedStorageUrl = getSignedAudioUrl;

/** Batch-sign many stored values (paths or legacy URLs) in ONE request. Returns a path -> URL map. */
export async function signStorageMap(
  bucket: string,
  stored: (string | null | undefined)[],
  ttl: number = SIGNED_AUDIO_TTL_SECONDS,
): Promise<Record<string, string>> {
  const map: Record<string, string> = {};
  const paths = Array.from(new Set(
    stored.map((s) => storagePathFromStored(s, bucket)).filter((p): p is string => !!p),
  ));
  if (paths.length === 0) return map;
  try {
    const client = getAuthenticatedClient();
    const { data } = await client.storage.from(bucket).createSignedUrls(paths, ttl);
    (data || []).forEach((d, i) => { if (d?.signedUrl) map[paths[i]] = d.signedUrl; });
  } catch { /* additive */ }
  return map;
}
